import React, { ReactNode, Suspense, useState } from 'react';
import { Card } from '../common/Card';
import { Button } from '../common/Button';
import { Spinner } from '../common/Spinner';

/**
 * `cockpit/ChartCard` (M-F, `architecture-ux-density-v2.md` §8/§10.5).
 *
 * Generischer Kachel-Rahmen für die Cockpit-Charts — trägt die
 * `useCockpitTile`-Isolation (Lade-/Fehler-/Bereit-Zustand + Retry, Muster
 * `pages/CockpitPage.tsx#TileBody`) UND den A11y-PFLICHT-Fallback
 * (§10.5 Punkt 5): eine umschaltbare Datentabellen-Ansicht, die zugleich
 * als Chart-Fehler-Fallback dient, falls der lazy-geladene Chart-Chunk zur
 * Laufzeit nicht rendert (CRA-Chunk-Load-Risiko nach Deploy, §8).
 *
 * `state` wird NICHT selbst geladen — der Aufrufer (CockpitPage) speist die
 * bereits von einer anderen Kachel geladenen Rohdaten ein (§6 „0 zusätzliche
 * Firestore-Reads"). `onRetry` löst einen isolierten Re-Fetch NUR dieser
 * Chart-Kachel aus (Kachel-Isolation bleibt auch im Fehlerfall gewahrt, ohne
 * dass ein Klick hier die Geschwister-Kachel mit-triggert).
 *
 * @module components/cockpit/ChartCard
 */

export type ChartLoadState<T> = { status: 'loading' } | { status: 'ready'; data: T } | { status: 'error' };

export interface ChartCardProps<T> {
  title: string;
  state: ChartLoadState<T>;
  onRetry: () => void;
  /** Sprechende Zusammenfassung mit Zahlen (§10.5 Punkt 5), aus `data` berechnet. */
  ariaLabel: (data: T) => string;
  /** Rendert den (ggf. lazy-geladenen) Chart. */
  renderChart: (data: T) => ReactNode;
  /** Rendert den PFLICHT-Datentabellen-Fallback (echte `<table>`). */
  renderTable: (data: T) => ReactNode;
  toggleToTableLabel: string;
  toggleToChartLabel: string;
  emptyDashLabel: string;
  retryLabel: string;
  /** Optionale Klick-Navigation der gesamten Kachel (z. B. Bucket → Zielseite). */
  onClick?: () => void;
  /**
   * Optionales Zusatz-Element neben dem Titel (z. B. ein `GrowthBadge` im
   * Umsatz-Chart-Header, M-CFO-5/E5). Rein additiv — bestehende Consumer, die
   * das Feld nicht setzen, rendern unverändert.
   */
  titleAccessory?: ReactNode;
  /**
   * Optionale Zeile(n) unter dem Kachel-Kopf (z. B. Untertitel + Abgrenzungssatz
   * der Netto-Cash-/Aging-Kachel, v5 §6.3). In ALLEN Zuständen sichtbar (Lade-/
   * Fehler-/Bereit), damit die beschreibende Abgrenzung nie flackert. Rein
   * additiv — bestehende Consumer, die das Feld nicht setzen, rendern unverändert.
   */
  subtitle?: ReactNode;
}

interface ChartErrorBoundaryState {
  hasError: boolean;
}

/**
 * Fängt Render-Fehler des lazy-geladenen Chart-Chunks ab (§8 „Chunk-Load-
 * Fehler nach Deploy = bekanntes CRA-Risiko") und meldet sie an den
 * Elternzustand, statt die Seite zu crashen. Rendert selbst `null` — der
 * Aufrufer (`ChartCard`) schaltet daraufhin auf die Tabellen-Ansicht um.
 */
class ChartErrorBoundary extends React.Component<{ children: ReactNode; onError: () => void }, ChartErrorBoundaryState> {
  constructor(props: { children: ReactNode; onError: () => void }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): ChartErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error): void {
    this.props.onError();
    // eslint-disable-next-line no-console -- kein `logger`-Import hier (reine Presentational-Komponente, kein Firestore/API-Kontext).
    console.error('ChartCard: Chart-Render fehlgeschlagen, Fallback auf Tabellen-Ansicht', error);
  }

  render(): ReactNode {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

/**
 * Generische Chart-Kachel: Lade-Spinner / „—"+Retry (Fehler) / Chart+Toggle
 * (bereit). `T` ist die vom Aufrufer bestimmte Chart-Datenform (z. B.
 * `MonthlyBucket[]` oder `AgingBucket[]`).
 */
export function ChartCard<T>({
  title,
  state,
  onRetry,
  ariaLabel,
  renderChart,
  renderTable,
  toggleToTableLabel,
  toggleToChartLabel,
  emptyDashLabel,
  retryLabel,
  onClick,
  titleAccessory,
  subtitle,
}: ChartCardProps<T>) {
  const [showTable, setShowTable] = useState(false);
  const [chartRenderFailed, setChartRenderFailed] = useState(false);

  const isClickable = typeof onClick === 'function';
  const tableForced = showTable || chartRenderFailed;

  return (
    <Card
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={isClickable ? (e) => e.key === 'Enter' && onClick?.() : undefined}
      className={isClickable ? 'cursor-pointer' : undefined}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <h3 className="text-sm font-semibold text-secondary">{title}</h3>
          {titleAccessory}
        </div>
        {state.status === 'ready' && !chartRenderFailed && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setShowTable((v) => !v);
            }}
            className="text-xs text-secondary hover:text-primary underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent rounded"
          >
            {showTable ? toggleToChartLabel : toggleToTableLabel}
          </button>
        )}
      </div>

      {subtitle && <div className="mb-2">{subtitle}</div>}

      {state.status === 'loading' && (
        <div className="flex justify-center py-6">
          <Spinner size="sm" />
        </div>
      )}

      {state.status === 'error' && (
        <div className="flex items-center justify-between gap-2">
          <span className="text-secondary">{emptyDashLabel}</span>
          <Button
            size="sm"
            variant="secondary"
            onClick={(e) => {
              e.stopPropagation();
              onRetry();
            }}
          >
            {retryLabel}
          </Button>
        </div>
      )}

      {state.status === 'ready' &&
        (tableForced ? (
          <div>{renderTable(state.data)}</div>
        ) : (
          <ChartErrorBoundary onError={() => setChartRenderFailed(true)}>
            <div role="img" aria-label={ariaLabel(state.data)}>
              <div aria-hidden="true">
                <Suspense
                  fallback={
                    <div className="flex justify-center py-6">
                      <Spinner size="sm" />
                    </div>
                  }
                >
                  {renderChart(state.data)}
                </Suspense>
              </div>
            </div>
          </ChartErrorBoundary>
        ))}
    </Card>
  );
}

export default ChartCard;
